import api from './api';

export interface WebhookFilterRule {
  field: string;
  operator: string;
  value?: unknown;
}

export interface WebhookFilters {
  match: 'all' | 'any';
  rules: WebhookFilterRule[];
}

export interface WebhookConfig {
  id: string;
  url: string;
  nombre?: string | null;
  descripcion?: string | null;
  hasSecret: boolean;
  eventos: string[];
  activo: boolean;
  maxReintentos: number;
  timeoutMs: number;
  headers?: Record<string, string> | null;
  emailAlertaFallos?: string | null;
  filtros?: WebhookFilters | null;
  fechaCreacion: string;
  fechaActualizacion: string;
}

export interface CreateWebhookConfigRequest {
  url: string;
  nombre?: string | null;
  descripcion?: string | null;
  secret?: string | null;
  eventos: string[];
  activo?: boolean;
  maxReintentos?: number;
  timeoutMs?: number;
  headers?: Record<string, string> | null;
  emailAlertaFallos?: string | null;
  filtros?: WebhookFilters | null;
}

export interface UpdateWebhookConfigRequest {
  url?: string;
  nombre?: string | null;
  descripcion?: string | null;
  secret?: string | null;
  clearSecret?: boolean;
  eventos?: string[];
  activo?: boolean;
  maxReintentos?: number;
  timeoutMs?: number;
  headers?: Record<string, string> | null;
  emailAlertaFallos?: string | null;
  clearEmailAlertaFallos?: boolean;
  filtros?: WebhookFilters | null;
  clearFiltros?: boolean;
}

export interface WebhookLog {
  id: string;
  webhookConfigId: string;
  evento: string;
  status: string;
  intentos: number;
  statusCode?: number | null;
  respuesta?: string | null;
  error?: string | null;
  payload?: unknown;
  headers?: unknown;
  manualRetryCount: number;
  lastManualRetryAt?: string | null;
  fechaEnvio?: string | null;
  fechaExito?: string | null;
  fechaCreacion: string;
}

export interface WebhookStats {
  total: number;
  exitosos: number;
  fallidos: number;
  pendientes: number;
  filtrados: number;
  tasaExito: number;
}

export interface WebhookEventCatalogItem {
  event: string;
  label: string;
  group: string;
  emittable: boolean;
}

export interface WebhookFilterField {
  field: string;
  label: string;
  operators: string[];
}

export interface WebhookLogQuery {
  fechaDesde?: string;
  fechaHasta?: string;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

export interface SyncClientesRequest {
  dryRun?: boolean;
  soloActivos?: boolean;
  omitirYaSincronizados?: boolean;
  delayMs?: number;
  webhookConfigId?: string | null;
}

export interface WebhookSyncJob {
  id: string;
  webhookConfigId?: string | null;
  status: string;
  total: number;
  enviados: number;
  omitidos: number;
  fallidos: number;
  cancelRequested: boolean;
  errores?: unknown;
  opciones?: unknown;
  errorMessage?: string | null;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  fechaCreacion: string;
}

const base = '/api/admin/webhooks';

const webhookService = {
  listConfigs: (page = 1, limit = 25) =>
    api.get<PagedResult<WebhookConfig>>(`${base}/configs`, { params: { page, limit } }).then((r) => r.data),

  getConfig: (id: string) =>
    api.get<WebhookConfig>(`${base}/configs/${id}`).then((r) => r.data),

  createConfig: (body: CreateWebhookConfigRequest) =>
    api.post<WebhookConfig>(`${base}/configs`, body).then((r) => r.data),

  updateConfig: (id: string, body: UpdateWebhookConfigRequest) =>
    api.put<WebhookConfig>(`${base}/configs/${id}`, body).then((r) => r.data),

  deleteConfig: (id: string) =>
    api.delete(`${base}/configs/${id}`).then((r) => r.data),

  getLogs: (id: string, page = 1, limit = 25, query?: WebhookLogQuery) =>
    api.get<PagedResult<WebhookLog>>(`${base}/configs/${id}/logs`, {
      params: { page, limit, fechaDesde: query?.fechaDesde, fechaHasta: query?.fechaHasta },
    }).then((r) => r.data),

  getStats: (id: string, query?: WebhookLogQuery) =>
    api.get<WebhookStats>(`${base}/configs/${id}/stats`, {
      params: { fechaDesde: query?.fechaDesde, fechaHasta: query?.fechaHasta },
    }).then((r) => r.data),

  retryLog: (configId: string, logId: string) =>
    api.post(`${base}/configs/${configId}/logs/${logId}/retry`).then((r) => r.data),

  getEvents: () =>
    api.get<WebhookEventCatalogItem[]>(`${base}/events`).then((r) => r.data),

  getFilterFields: () =>
    api.get<WebhookFilterField[]>(`${base}/filter-fields`).then((r) => r.data),

  testConfig: (id: string) =>
    api.post(`${base}/test/${id}`).then((r) => r.data),

  syncClientes: (body: SyncClientesRequest) =>
    api.post<WebhookSyncJob>(`${base}/sync/clientes`, body).then((r) => r.data),

  getSyncJob: (jobId: string) =>
    api.get<WebhookSyncJob>(`${base}/sync/clientes/${jobId}`).then((r) => r.data),

  cancelSyncJob: (jobId: string) =>
    api.post(`${base}/sync/clientes/${jobId}/cancel`).then((r) => r.data),
};

export default webhookService;
